import { defineStore } from "pinia";
import { ref } from "vue";

export const useContentStore = defineStore("contentStore", () => {
    const content = ref({});

    const LOADING_STEPS = 4;
    const isLoading = ref(true);
    const loadingState = ref({
        progress: 0,
        message: 'Initializing'
    });

    async function loadContent(skipMediaPreload) {
        isLoading.value = true;

        updateLoadingState(0, "Connecting to server");

        const response = await fetch("/api/salestool/content");

        updateLoadingState(1, "Fetching Content");

        const json = await response.json();

        if(!skipMediaPreload){
            updateLoadingState(2, "Find media items to preload");

            const mediaItems = {
                images: [
                    // Fix images defined in vue
                    "/bundles/salestool/assets/images/logo.svg",
                    "/bundles/salestool/assets/images/start.png",
                    "/bundles/salestool/assets/images/progress_indicator.png",
                ],
                videos: [],
            };

            findMediaItems(json, mediaItems);

            updateLoadingState(3, "Preloading Images");

            await preloadImages(mediaItems.images);

            updateLoadingState(4, "Preloading Videos");

            await preloadVideos(mediaItems.videos);
        }

        content.value = json.data;
        isLoading.value = false;
    }

    function findMediaItems(obj, mediaItems) {
        for (const key in obj) {
            if (!obj[key]) {
                continue;
            }

            // Language flags
            if(key === 'flag'){
                mediaItems.images.push(obj.flag);
                continue;
            }

            // Qr Codes (SVG)
            if (key === "qrCode") {
                Object.values(obj.qrCode).forEach((qrCode) => {
                    mediaItems.images.push(qrCode);
                });
                continue;
            }

            // Image
            if (obj[key].type === "image") {
                if (!mediaItems.images.includes(obj[key].src)) {
                    mediaItems.images.push(obj[key].src);
                }

                if (obj[key].lightboxSrc && !mediaItems.images.includes(obj[key].lightboxSrc)) {
                    mediaItems.images.push(obj[key].lightboxSrc);
                }

                continue;
            }

            // Video
            if (obj[key].type === "video") {
                const videoSrc = obj[key].src;
                const posterSrc = obj[key].poster;

                if (!mediaItems.videos.includes(videoSrc)) {
                    mediaItems.videos.push(videoSrc);
                }

                if (posterSrc && !mediaItems.images.includes(posterSrc)) {
                    mediaItems.images.push(posterSrc);
                }

                continue;
            }

            // Object -> call function recursively
            if (typeof obj[key] === "object") {
                findMediaItems(obj[key], mediaItems);
            }
        }
    }

    async function preloadVideos(videos) {
        for (const src of videos) {
            await preloadVideo(src);
        }
    }

    async function preloadVideo(videoPath) {
        return new Promise((resolve, reject) => {
            const video = document.createElement("video"); // Neues Video-Element erstellen

            video.onloadeddata = () => resolve();

            video.onerror = (error) => reject(new Error(`Failed to load video at ${videoPath}: ${error}`));

            video.src = videoPath;

            video.load();
        });
    }

    async function preloadImages(images) {
        for (const src of images) {
            await preloadImage(src);
        }
    }

    async function preloadImage(imagePath) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = (error) => reject(new Error(`Failed to load image at ${imagePath}: ${error}`));
            img.src = imagePath;
        });
    }

        function updateLoadingState(progress, message) {
        loadingState.value = {
            progress: progress / LOADING_STEPS,
            message,
        };
    }

    return {
        content,
        isLoading,
        loadingState,
        loadContent,
    };
});
